import { useEffect, useMemo, useRef, useState } from "react"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { FULL_SCREEN_BTN } from "minimals-template/components/editor/editor-buttons"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Bound } from "lib/@components/binding/Bound"
import { Box, InputAdornment, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { useParams } from "react-router"
import {
    SkillSetArea,
    TaskRowAfter,
    TaskRowBefore,
    TaskRowEnd,
    TimeAndFrequency,
    TreeToolbar,
    UOMArea,
} from "slot-definitions"
import useToggle from "minimals-template/components/@hooks/useToggle"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useSave } from "lib/@hooks/use-save"
import { useProject } from "authoring/lib/hooks/use-project"
import { sanitizeTask } from "./utilities/task-utils"
import { useAddGroupAmends } from "../add/use-add-group-amends"
import { taskStyles } from "./task-editor-styles"
import { Task } from "routes/schedule/tasks/task"
import { Repeat } from "lib/@components/repeat"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { frag } from "lib/@components/slot/frag"
import { DeleteTaskButton } from "./plugins/delete-task-button"
import { deriveGuidFrom, generate } from "library/guid"
import { AddTaskButton } from "./components/add-task-button"
import { PreviewButton } from "./plugins/preview-button"
import { BackButton } from "routes/schedule/tree/schedules/upDirectory"
import { Else } from "lib/@components/else"
import { getProjectGroup } from "../controller/get-project-group"
import { updateProjectGroup } from "../controller/update-project-group"
import { useDialog } from "lib/@hooks/useDialog"
import { AuthoringSkillSet } from "./plugins/authoring-skill-set"
import { Frequency } from "routes/schedule/tasks/frequency"
import { Time } from "routes/schedule/tasks/time"
import { navigateGroup } from "../../utilities/project-navigation"
import { AuthoringDeleteDialog } from "../../plugins/project-item/authoring-delete-dialog"
import { AmendsBoundary } from "authoring/routes/project/amends/components/amends-boundary"
import { AmendsBoundaryTaskButton } from "authoring/routes/project/amends/components/amends-boundary-task-button"
import { AmendsBoundaryTaskEditor } from "authoring/routes/project/amends/components/amends-boundary-task-editor"
import { AmendsMissingTaskHint } from "authoring/routes/project/amends/components/amends-missing-task-hint"
import { getTaskAmendsKey, getTaskId } from "authoring/routes/project/amends/lib/task-amends-key"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import {
    collectMacroTokensFromSchedule,
    extractMacroTokensFromString,
} from "authoring/routes/macro/lib/macro-token-utils"
import { getAudienceAmendsContents } from "authoring/routes/project/amends/lib/audience-aware-amends.mjs"
import { buildScheduleContent } from "authoring/routes/macro/lib/macro-conversion"
import { ScheduleMacroAutocomplete } from "authoring/routes/project/schedule-editor/schedule-macro-autocomplete"
import { useChipExpander } from "authoring/routes/macro/hooks/use-chip-expander"
import { PreviewTaskAmends } from "./components/preview-task-amends"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { AuthoringHeaderBarRight } from "authoring/routes/@layouts/authoring-app-layout"
import { buildEditableTasks } from "authoring/routes/macro/lib/build-editable-authoring-value"
import { stripChipHtml } from "authoring/routes/macro/lib/macro-chip-utils"
import { hasDemand } from "lib/authorization/has-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function AddTask() {
    const { projectId, groupId } = useParams()
    const { project, loading: projectLoading } = useProject({ id: projectId })
    const { data: projectGroup, loading: groupLoading } = getProjectGroup.useResults.status({
        projectId,
        groupId,
    })
    const groupLoadedRef = useRef(false)
    if (projectGroup) groupLoadedRef.current = true
    const { toggle: previewTask, onToggle: togglePreview } = useToggle(false)
    const authoringDeleteDialog = useDialog(
        <AuthoringDeleteDialog
            value={true}
            title={"Delete Task"}
            message={"Are you sure you want to remove this task? This cannot be undone."}
        />,
        { maxWidth: "sm" }
    )

    const group = project?.groups?.find(({ id }) => id === groupId)
    const { newTasks: existingTasks } = group ?? {}
    const [newTasks, setNewTasks] = useState(() => [getNewTask()])
    const [saveReady, setSaveReady] = useState(false)
    const { amends, saveAmend, deleteAmend, options } = useAddGroupAmends({
        projectId,
        groupId,
        newTasks,
    })
    const sourceTasks = saveReady ? newTasks : existingTasks?.length ? existingTasks : newTasks
    const tokenIdsRef = useRef({ key: "", tokenIds: [] })
    const nextTokenIds = collectAddTaskTokenIds(sourceTasks, amends)
    const nextTokenIdsKey = nextTokenIds.join("|")
    if (tokenIdsRef.current.key !== nextTokenIdsKey) {
        tokenIdsRef.current = { key: nextTokenIdsKey, tokenIds: nextTokenIds }
    }
    const {
        macros: allMacros,
        macroMap,
        loading: macrosLoading,
    } = useEnrichedMacroMap({
        tokenIds: tokenIdsRef.current.tokenIds,
    })
    const { handleChipClick, chipExpanderPopover } = useChipExpander({ macroMap })
    const componentTokenTargetRef = useRef(null)
    const refresh = useRefresh()
    const onChange = useSave({ save: saveTasksToGroup, target: newTasks, id: "add-task", wait: 1000 }, [
        projectId,
        groupId,
    ])
    const prevTaskCountRef = useRef(newTasks.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const expandedTasks = useMemo(expandTaskMacros, [newTasks, macroMap, previewTask])
    const isInitialLoading =
        !groupLoadedRef.current && ((projectLoading && !project) || (groupLoading && !projectGroup))
    const canEditSchedule = hasDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)

    useEffect(initializeTasks, [projectLoading, groupLoading, existingTasks, macrosLoading, macroMap, saveReady])
    useEffect(triggerSaveOnTaskCountChange, [newTasks.length, onChange, saveReady])

    TaskRowEnd.usePlug(
        replaceAllWith(
            <>
                <AmendsBoundaryTaskButton />
                <DeleteTaskButton onClick={deleteTask} disabled={newTasks?.length === 1} />
            </>
        ),
        [newTasks]
    )
    TaskRowAfter.usePlug(
        <>
            <AmendsBoundaryTaskEditor />
            <PreviewTaskAmends />
        </>
    )
    TaskRowBefore.usePlug(<AmendsMissingTaskHint />)
    UOMArea.usePlug(replaceAllWith(frag))
    TreeToolbar.usePlug(
        <If truthy={!previewTask} priority={100}>
            <PreviewButton onClick={togglePreview} disabled={!newTasks?.length} />
        </If>,
        [previewTask, newTasks]
    )
    SkillSetArea.usePlug(replaceAllWith(<AuthoringSkillSet />))
    TimeAndFrequency.usePlug(
        replaceAllWith(
            <>
                <Time
                    slotProps={{
                        input: {
                            inputMode: "numeric",
                            onBeforeInput: (e) => {
                                const data = e.data ?? e.nativeEvent?.data
                                if (!/^[0-9:]$/.test(data) || (data === ":" && e.target.value.includes(":"))) {
                                    e.preventDefault()
                                }
                            },
                            endAdornment: (
                                <InputAdornment data-cy="minutes-display-helper" position="end">
                                    min
                                </InputAdornment>
                            ),
                        },
                        inputLabel: {
                            shrink: true,
                        },
                    }}
                    sx={{ width: 150 }}
                    placeholder={"unspecified"}
                />
                <Frequency />
            </>
        )
    )

    if (isInitialLoading) return <LoadingScreen />
    if (!canEditSchedule || projectGroup?.readOnly) return navigateGroup({ projectId, groupId })
    if (!saveReady && existingTasks?.length && macrosLoading) return <LoadingScreen />

    return (
        <PageBreadcrumbContainer
            title={previewTask ? "Preview Tasks" : "Add Tasks"}
            backButton={<BackButton onClick={previewTask ? togglePreview : undefined} />}
        >
            <Box flex={1} pt={2} pb={5}>
                <AmendsBoundary
                    amends={amends}
                    onSaveAmend={previewTask ? noopAmend : saveAmend}
                    onDeleteAmend={previewTask ? noopAmend : deleteAmend}
                    options={options}
                >
                    <If value={newTasks && newTasks?.length > 0 && !previewTask}>
                        <SaveIndicator id="add-task" slot={AuthoringHeaderBarRight} />
                        <Box onClickCapture={handleChipClick}>
                            <Bound
                                editMode={true}
                                isAuthoringScheduleEditor={true}
                                editorAddBtns={[FULL_SCREEN_BTN]}
                                hideDrag={true}
                                onChange={handleChange}
                                alwaysOpenTask={true}
                                getTaskAmendsKey={getTaskAmendsKey}
                                macroMap={macroMap}
                                onComponentTokenInserted={handleComponentTokenInserted}
                                onRegisterComponentTokenTarget={registerComponentTokenTarget}
                                onClearComponentTokenTarget={clearComponentTokenTarget}
                            >
                                <Repeat keyFn={"temporaryTaskId"} list={newTasks} item={<Task sx={taskStyles} />} />
                            </Bound>
                        </Box>
                        <ScheduleMacroAutocomplete macros={allMacros} enabled={true} isPreviewMode />
                        <AddTaskButton onClick={addTask} />
                        <Else>
                            <Bound
                                editMode={false}
                                hideDrag={true}
                                alwaysOpenTask={true}
                                getTaskAmendsKey={getTaskAmendsKey}
                                macroMap={macroMap}
                            >
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    {expandedTasks?.length} {expandedTasks?.length === 1 ? "task" : "tasks"} to add
                                </Typography>
                                <Box className="schedule-details styled-bullets">
                                    <Repeat
                                        keyFn={"temporaryTaskId"}
                                        list={expandedTasks}
                                        item={<Task sx={taskStyles} />}
                                    />
                                </Box>
                            </Bound>
                        </Else>
                    </If>
                </AmendsBoundary>
            </Box>
            {chipExpanderPopover}
        </PageBreadcrumbContainer>
    )

    function noopAmend() {
        return null
    }

    function handleComponentTokenInserted(componentId) {
        componentTokenTargetRef.current?.onComponentTokenInserted?.(componentId)
    }

    function registerComponentTokenTarget(target) {
        componentTokenTargetRef.current = target ?? null
    }

    function clearComponentTokenTarget() {
        componentTokenTargetRef.current = null
    }

    function collectAddTaskTokenIds(tasks, amendsArr) {
        const tokenIds = new Set()
        if (tasks?.length) collectMacroTokensFromSchedule({ tasks }, tokenIds)
        for (const amend of amendsArr ?? []) {
            for (const content of getAudienceAmendsContents(amend?.payload)) {
                for (const tokenId of extractMacroTokensFromString(content)) {
                    tokenIds.add(tokenId)
                }
            }
        }
        return [...tokenIds]
    }

    function expandTaskMacros() {
        if (!newTasks?.length || !macroMap) return newTasks
        const expanded = buildScheduleContent({ tasks: newTasks }, macroMap)
        return expanded?.tasks ?? newTasks
    }

    function initializeTasks() {
        if (saveReady) return
        if (projectLoading || groupLoading) return
        if (existingTasks?.length && macrosLoading) return
        if (existingTasks?.length) {
            setNewTasks(buildEditableTasks(existingTasks, macroMap))
            prevTaskCountRef.current = existingTasks.length
        }
        setSaveReady(true)
    }

    function triggerSaveOnTaskCountChange() {
        if (!saveReady) return
        if (prevTaskCountRef.current === newTasks.length) return
        prevTaskCountRef.current = newTasks.length
        onChange()
    }

    function handleChange() {
        refresh()
        if (saveReady) onChange()
    }

    async function saveTasksToGroup(tasks) {
        if (!projectId || !groupId || !tasks?.length) return true
        const cleanedTasks = tasks.map((task) => {
            const cleanedTask = sanitizeTask({
                ...task,
                frequency: { ...task.frequency, interval: Number(task.frequency?.interval) || 0 },
            })
            if (cleanedTask.title) cleanedTask.title = stripChipHtml(cleanedTask.title)
            if (cleanedTask.content) cleanedTask.content = stripChipHtml(cleanedTask.content)
            if (cleanedTask.note) cleanedTask.note = stripChipHtml(cleanedTask.note)
            return cleanedTask
        })
        const saved = await updateProjectGroup({ projectId, group: { id: groupId, newTasks: cleanedTasks } })
        if (!saved) throw new Error("Failed to save tasks")
        return true
    }

    function addTask() {
        setNewTasks((tasks) => [...tasks, getNewTask()])
    }

    async function deleteTask(targetId) {
        const confirm = await authoringDeleteDialog()
        if (!confirm) return

        setNewTasks((tasks) => tasks.filter((task) => getTaskId(task) !== targetId))
    }

    function getNewTask() {
        const temporaryTaskId = deriveGuidFrom(generate())
        return {
            temporaryTaskId,
            classification: "Amber",
            frequency: {
                period: "Unknown",
                interval: 0,
            },
            title: "",
            note: "",
            content: "",
            isHeader: false,
            unitOfMeasure: "Nr",
            skillSet: "1",
        }
    }
}
